import { useEffect, useState } from 'react';
import type { Project } from '@proofroom/schema';
import { beat, type BeatJobPage } from '../api/endpoints';

export function BeatJobPicker({ project, onSaved, onClose }: { project?: Project; onSaved(project: Project): void; onClose(): void }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState('');
  const [result, setResult] = useState<BeatJobPage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    beat.status().then((s) => { if (alive) setEnabled(s.enabled); }).catch((err) => { if (alive) setError(err instanceof Error ? err.message : 'Could not connect to Beat'); });
    return () => { alive = false; };
  }, []);
  const search = async (text: string, page = 0) => {
    setBusy(true); setError('');
    try { setResult(await beat.jobs(text, page)); setSearched(text); }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not search Beat'); }
    finally { setBusy(false); }
  };
  return <section className="card beat-picker" aria-label={project ? 'Link a Beat job' : 'Create project from Beat'}>
    <div className="btn-row"><h2>{project ? 'Link a Beat job' : 'Create project from Beat'}</h2><button className="btn sm ghost" onClick={onClose} disabled={busy}>Close</button></div>
    <p className="muted small">{project ? 'Select the job to link. Your existing project name, client and members stay the same; an unassigned client is filled from Beat.' : 'Choose a job to create its client and project in Proofroom. A job already linked opens its existing project.'}</p>
    {enabled === false && <p role="status">Beat is not configured. Ask an administrator to configure the server connection.</p>}
    <form className="btn-row" onSubmit={(e) => { e.preventDefault(); void search(query.trim()); }}>
      <input className="input" aria-label="Search Beat jobs" placeholder="Job number, name or client" value={query} maxLength={100} onChange={(e) => setQuery(e.target.value)} disabled={busy} />
      <button className="btn" disabled={!enabled || busy || !query.trim()}>{busy ? 'Working…' : 'Search Beat'}</button>
    </form>
    {error && <p className="error-text" role="alert">{error}</p>}
    {result && <>
      <p className="muted small">{result.total} matching jobs in Beat's cached data</p>
      {result.jobs.length === 0 && <p>No matching jobs. Try another job number or client.</p>}
      <div className="list">{result.jobs.map((job) => <div className="row" key={job.uuid}>
        <div className="grow"><strong>{job.number} · {job.name}</strong><div className="meta">{job.clientName ?? 'No client'}{job.status ? ` · ${job.status}` : ''}</div></div>
        <button className="btn sm primary" disabled={busy} onClick={async () => {
          if (project?.beatJobUuid && project.beatJobUuid !== job.uuid && !window.confirm('Replace this project’s Beat job? Any review to-do already added to the previous Beat job will stay there.')) return;
          setBusy(true); setError('');
          try { onSaved(project ? await beat.link(project.id, job.uuid) : await beat.createProject(job.uuid)); }
          catch (err) { setError(err instanceof Error ? err.message : 'Could not link the Beat job'); }
          finally { setBusy(false); }
        }}>{project ? 'Link job' : 'Create / open project'}</button>
      </div>)}</div>
      <div className="btn-row"><button className="btn sm" disabled={busy || result.page === 0} onClick={() => void search(searched, result.page - 1)}>Previous</button>
        <span className="muted small">Page {result.page + 1}</span><button className="btn sm" disabled={busy || (result.page + 1) * result.limit >= result.total || result.page >= 10000} onClick={() => void search(searched, result.page + 1)}>Next</button></div>
    </>}
  </section>;
}

export function BeatBacklink({ project }: { project: Project }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  return <div className="beat-backlink">
    <button className="btn" disabled={busy} onClick={async () => {
      if (!window.confirm(`Create a review to-do in Beat job ${project.beatJobNumber} with a link to this Proofroom project and its documents?`)) return;
      setBusy(true); setError(''); setMessage('');
      try { const result = await beat.backlink(project.id); setMessage(result.alreadyAdded ? 'This project link has already been added in Beat.' : 'Project link added as a review to-do in Beat.'); }
      catch (err) { setError(err instanceof Error ? err.message : 'Could not add the link. Retry uses the same request.'); }
      finally { setBusy(false); }
    }}>{busy ? 'Adding link…' : 'Add Proofroom link in Beat'}</button>
    <p className="muted small">Creates one review to-do in the linked Beat job. Retrying does not create another.</p>
    {message && <p role="status">{message}</p>}{error && <p role="alert" className="error-text">{error}</p>}
  </div>;
}
